import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  writeBatch,
  setDoc
} from 'firebase/firestore'
import { db } from '../config/firebase'
import type { Organization, OrganizationMember } from '../store/organizationAtoms'
import type { ChartData } from '../store/chartAtoms'
import type { User, CreateUser, UpdateUser } from '@shared/schemas/user'

// Organization Services
export const organizationService = {
  // Create a new organization
  async create(data: {
    name: string
    slug: string
    description?: string
    website?: string
    ownerId: string
  }): Promise<Organization> {
    const orgData = {
      ...data,
      memberIds: [data.ownerId], // Include the owner in the memberIds array
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      memberCount: 1,
      plan: 'free' as const,
      settings: {
        publicJoin: false,
        requireApproval: true,
        maxMembers: 5
      }
    }

    const docRef = await addDoc(collection(db, 'organizations'), orgData)
    
    // Add organization to user's organization list
    await userService.addOrganization(data.ownerId, docRef.id)
    
    const createdDoc = await getDoc(docRef)
    return {
      id: docRef.id,
      ...createdDoc.data(),
      createdAt: createdDoc.data()?.createdAt?.toDate() || new Date(),
      updatedAt: createdDoc.data()?.updatedAt?.toDate() || new Date()
    } as Organization
  },

  // Get organization by ID
  async getById(id: string): Promise<Organization | null> {
    const docRef = doc(db, 'organizations', id)
    const docSnap = await getDoc(docRef)
    
    if (!docSnap.exists()) return null
    
    return {
      id: docSnap.id,
      ...docSnap.data(),
      createdAt: docSnap.data().createdAt?.toDate() || new Date(),
      updatedAt: docSnap.data().updatedAt?.toDate() || new Date()
    } as Organization
  },

  // Get organization by slug
  async getBySlug(slug: string): Promise<Organization | null> {
    const q = query(
      collection(db, 'organizations'),
      where('slug', '==', slug),
      limit(1)
    )
    
    const querySnapshot = await getDocs(q)
    if (querySnapshot.empty) return null
    
    const doc = querySnapshot.docs[0]
    return {
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    } as Organization
  },

  // Update organization
  async update(id: string, data: Partial<Organization>): Promise<void> {
    const docRef = doc(db, 'organizations', id)
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp()
    })
  },

  // Check if slug is available
  async isSlugAvailable(slug: string): Promise<boolean> {
    const org = await this.getBySlug(slug)
    return org === null
  },

  // Update organization logo
  async updateLogo(id: string, type: 'light' | 'dark', logoData: { url: string; path: string }): Promise<void> {
    const docRef = doc(db, 'organizations', id)
    
    // Get current organization data to preserve existing logos
    const orgDoc = await getDoc(docRef)
    if (!orgDoc.exists()) {
      throw new Error('Organization not found')
    }
    
    const currentData = orgDoc.data()
    const currentLogos = currentData.logos || {}
    
    // Update the specific logo type
    const updatedLogos = {
      ...currentLogos,
      [type]: logoData
    }
    
    await updateDoc(docRef, {
      logos: updatedLogos,
      updatedAt: serverTimestamp()
    })
  },

  // Remove organization logo
  async removeLogo(id: string, type: 'light' | 'dark'): Promise<void> {
    const docRef = doc(db, 'organizations', id)
    
    // Get current organization data
    const orgDoc = await getDoc(docRef)
    if (!orgDoc.exists()) {
      throw new Error('Organization not found')
    }
    
    const currentData = orgDoc.data()
    const currentLogos = currentData.logos || {}
    
    // Remove the specific logo type
    const updatedLogos = { ...currentLogos }
    delete updatedLogos[type]
    
    await updateDoc(docRef, {
      logos: updatedLogos,
      updatedAt: serverTimestamp()
    })
  },

  // Custom Color Palette Management for Organizations
  async saveCustomPalette(organizationId: string, palette: {
    name: string;
    description?: string;
    colors: string[];
    createdAt: Date;
    updatedAt: Date;
  }): Promise<{ id: string }> {
    const orgDocRef = doc(db, 'organizations', organizationId)
    const orgDoc = await getDoc(orgDocRef)
    
    if (!orgDoc.exists()) {
      throw new Error('Organization not found')
    }
    
    const paletteId = crypto.randomUUID()
    const currentPalettes = orgDoc.data().customColorPalettes || []
    
    const newPalette = {
      id: paletteId,
      ...palette,
      createdAt: new Date(), // Use regular Date instead of serverTimestamp() inside arrays
      updatedAt: new Date()
    }
    
    await updateDoc(orgDocRef, {
      customColorPalettes: [...currentPalettes, newPalette],
      updatedAt: serverTimestamp()
    })
    
    return { id: paletteId }
  },

  async updateCustomPalette(organizationId: string, paletteId: string, updates: {
    name?: string;
    description?: string;
    colors?: string[];
    updatedAt: Date;
  }): Promise<void> {
    const orgDocRef = doc(db, 'organizations', organizationId)
    const orgDoc = await getDoc(orgDocRef)
    
    if (!orgDoc.exists()) {
      throw new Error('Organization not found')
    }
    
    const currentPalettes = orgDoc.data().customColorPalettes || []
    const paletteIndex = currentPalettes.findIndex((p: any) => p.id === paletteId)
    
    if (paletteIndex === -1) {
      throw new Error('Palette not found')
    }
    
    const updatedPalettes = [...currentPalettes]
    updatedPalettes[paletteIndex] = {
      ...updatedPalettes[paletteIndex],
      ...updates,
      updatedAt: new Date() // Use regular Date instead of serverTimestamp() inside arrays
    }
    
    await updateDoc(orgDocRef, {
      customColorPalettes: updatedPalettes,
      updatedAt: serverTimestamp()
    })
  },

  async deleteCustomPalette(organizationId: string, paletteId: string): Promise<void> {
    const orgDocRef = doc(db, 'organizations', organizationId)
    const orgDoc = await getDoc(orgDocRef)
    
    if (!orgDoc.exists()) {
      throw new Error('Organization not found')
    }
    
    const currentPalettes = orgDoc.data().customColorPalettes || []
    const updatedPalettes = currentPalettes.filter((p: any) => p.id !== paletteId)
    
    await updateDoc(orgDocRef, {
      customColorPalettes: updatedPalettes,
      updatedAt: serverTimestamp()
    })
  },

  async getCustomPalettes(organizationId: string): Promise<any[]> {
    const orgDocRef = doc(db, 'organizations', organizationId)
    const orgDoc = await getDoc(orgDocRef)
    
    if (!orgDoc.exists()) {
      return []
    }
    
    const palettes = orgDoc.data().customColorPalettes || []
    return palettes.map((palette: any) => ({
      ...palette,
      createdAt: palette.createdAt?.toDate() || new Date(),
      updatedAt: palette.updatedAt?.toDate() || new Date()
    }))
  }
}

// Organization Member Services
export const memberService = {
  // Create a new member
  async create(data: {
    organizationId: string
    userId: string
    email: string
    displayName?: string
    role: OrganizationMember['role']
    status: OrganizationMember['status']
    invitedBy?: string
  }): Promise<OrganizationMember> {
    const memberData = {
      ...data,
      joinedAt: serverTimestamp()
    }

    // Use a batch to create member and update organization memberIds
    const batch = writeBatch(db)
    
    // Add member document
    const memberRef = doc(collection(db, 'organization_members'))
    batch.set(memberRef, memberData)
    
    // Update organization memberIds if status is active
    if (data.status === 'active') {
      const orgRef = doc(db, 'organizations', data.organizationId)
      const orgDoc = await getDoc(orgRef)
      
      if (orgDoc.exists()) {
        const currentMemberIds = orgDoc.data().memberIds || []
        if (!currentMemberIds.includes(data.userId)) {
          batch.update(orgRef, {
            memberIds: [...currentMemberIds, data.userId],
            memberCount: currentMemberIds.length + 1,
            updatedAt: serverTimestamp()
          })
        }
      }
      
      // Add organization to user's organization list
      await userService.addOrganization(data.userId, data.organizationId)
    }
    
    await batch.commit()
    
    const createdDoc = await getDoc(memberRef)
    return {
      id: memberRef.id,
      ...createdDoc.data(),
      joinedAt: createdDoc.data()?.joinedAt?.toDate() || new Date()
    } as OrganizationMember
  },

  // Get user's membership in organization
  async getUserMembership(userId: string, organizationId: string): Promise<OrganizationMember | null> {
    const q = query(
      collection(db, 'organization_members'),
      where('userId', '==', userId),
      where('organizationId', '==', organizationId),
      limit(1)
    )
    
    const querySnapshot = await getDocs(q)
    if (querySnapshot.empty) return null
    
    const doc = querySnapshot.docs[0]
    return {
      id: doc.id,
      ...doc.data(),
      joinedAt: doc.data().joinedAt?.toDate() || new Date()
    } as OrganizationMember
  },

  // Get user's organizations
  async getUserOrganizations(userId: string): Promise<{ organization: Organization; membership: OrganizationMember }[]> {
    const q = query(
      collection(db, 'organization_members'),
      where('userId', '==', userId),
      where('status', '==', 'active')
    )
    
    const querySnapshot = await getDocs(q)
    const results = []
    
    for (const memberDoc of querySnapshot.docs) {
      const membership = {
        id: memberDoc.id,
        ...memberDoc.data(),
        joinedAt: memberDoc.data().joinedAt?.toDate() || new Date()
      } as OrganizationMember
      
      const organization = await organizationService.getById(membership.organizationId)
      if (organization) {
        results.push({ organization, membership })
      }
    }
    
    return results
  },

  // Get organization members
  async getOrganizationMembers(organizationId: string): Promise<OrganizationMember[]> {
    const q = query(
      collection(db, 'organization_members'),
      where('organizationId', '==', organizationId),
      orderBy('joinedAt', 'desc')
    )
    
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      joinedAt: doc.data().joinedAt?.toDate() || new Date()
    } as OrganizationMember))
  }
}

// Chart Services
export const chartService = {
  // Save chart to Firestore
  async save(organizationId: string, chartData: Omit<ChartData, 'id' | 'createdAt'>): Promise<ChartData> {
    const data = {
      ...chartData,
      organizationId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }

    const docRef = await addDoc(collection(db, 'charts'), data)
    
    const createdDoc = await getDoc(docRef)
    return {
      id: docRef.id,
      ...createdDoc.data(),
      createdAt: createdDoc.data()?.createdAt?.toDate() || new Date()
    } as ChartData
  },

  // Get organization charts
  async getOrganizationCharts(organizationId: string): Promise<ChartData[]> {
    const q = query(
      collection(db, 'charts'),
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    )
    
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    } as ChartData))
  },

  // Update chart
  async update(chartId: string, data: Partial<ChartData>): Promise<void> {
    const docRef = doc(db, 'charts', chartId)
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp()
    })
  },

  // Delete chart
  async delete(chartId: string): Promise<void> {
    const docRef = doc(db, 'charts', chartId)
    await deleteDoc(docRef)
  }
}

// User Services
export const userService = {
  // Create or update user profile (upsert)
  async createOrUpdate(userId: string, userData: CreateUser): Promise<User> {
    const userDocRef = doc(db, 'users', userId)
    
    // Check if user already exists
    const existingDoc = await getDoc(userDocRef)
    
    const now = serverTimestamp()
    const data = {
      id: userId,
      ...userData,
      organizationIds: existingDoc.exists() ? existingDoc.data().organizationIds || [] : [],
      currentOrganizationId: existingDoc.exists() ? existingDoc.data().currentOrganizationId : null,
      preferences: {
        emailNotifications: true,
        pushNotifications: true,
        marketingEmails: false,
        weeklyDigest: true
      },
      emailVerified: existingDoc.exists() ? existingDoc.data().emailVerified || false : false,
      onboardingCompleted: existingDoc.exists() ? existingDoc.data().onboardingCompleted || false : false,
      lastLoginAt: now,
      createdAt: existingDoc.exists() ? existingDoc.data().createdAt : now,
      updatedAt: now
    }

    await setDoc(userDocRef, data, { merge: true })
    
    const updatedDoc = await getDoc(userDocRef)
    return {
      ...updatedDoc.data(),
      createdAt: updatedDoc.data()?.createdAt?.toDate() || new Date(),
      updatedAt: updatedDoc.data()?.updatedAt?.toDate() || new Date(),
      lastLoginAt: updatedDoc.data()?.lastLoginAt?.toDate() || new Date()
    } as User
  },

  // Get user by ID
  async getById(userId: string): Promise<User | null> {
    const userDocRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userDocRef)
    
    if (!userDoc.exists()) return null
    
    const data = userDoc.data()
    return {
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      lastLoginAt: data.lastLoginAt?.toDate() || new Date()
    } as User
  },

  // Update user profile
  async update(userId: string, updates: UpdateUser): Promise<void> {
    const userDocRef = doc(db, 'users', userId)
    
    // Use setDoc with merge: true to automatically handle missing fields
    await setDoc(userDocRef, {
      ...updates,
      updatedAt: serverTimestamp()
    }, { merge: true })
  },

  // Update user's current organization
  async setCurrentOrganization(userId: string, organizationId: string | null): Promise<void> {
    const userDocRef = doc(db, 'users', userId)
    
    await setDoc(userDocRef, {
      currentOrganizationId: organizationId,
      updatedAt: serverTimestamp()
    }, { merge: true })
  },

  // Add organization to user's organization list
  async addOrganization(userId: string, organizationId: string): Promise<void> {
    const userDocRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userDocRef)
    
    if (userDoc.exists()) {
      const currentOrganizationIds = userDoc.data().organizationIds || []
      if (!currentOrganizationIds.includes(organizationId)) {
        await setDoc(userDocRef, {
          organizationIds: [...currentOrganizationIds, organizationId],
          currentOrganizationId: userDoc.data().currentOrganizationId || organizationId, // Set as current if no current org
          updatedAt: serverTimestamp()
        }, { merge: true })
      }
    }
  },

  // Remove organization from user's organization list
  async removeOrganization(userId: string, organizationId: string): Promise<void> {
    const userDocRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userDocRef)
    
    if (userDoc.exists()) {
      const currentOrganizationIds = userDoc.data().organizationIds || []
      const updatedOrganizationIds = currentOrganizationIds.filter((id: string) => id !== organizationId)
      
      const updates: any = {
        organizationIds: updatedOrganizationIds,
        updatedAt: serverTimestamp()
      }
      
      // If removing the current organization, clear it or set to another org
      if (userDoc.data().currentOrganizationId === organizationId) {
        updates.currentOrganizationId = updatedOrganizationIds.length > 0 ? updatedOrganizationIds[0] : null
      }
      
      await setDoc(userDocRef, updates, { merge: true })
    }
  },

  // Update user preferences
  async updatePreferences(userId: string, preferences: Partial<User['preferences']>): Promise<void> {
    const userDocRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userDocRef)
    
    if (userDoc.exists()) {
      const currentPreferences = userDoc.data().preferences || {}
      await setDoc(userDocRef, {
        preferences: {
          ...currentPreferences,
          ...preferences
        },
        updatedAt: serverTimestamp()
      }, { merge: true })
    }
  },

  // Mark onboarding as completed
  async completeOnboarding(userId: string): Promise<void> {
    const userDocRef = doc(db, 'users', userId)
    
    await setDoc(userDocRef, {
      onboardingCompleted: true,
      updatedAt: serverTimestamp()
    }, { merge: true })
  },

  // Update last login time
  async updateLastLogin(userId: string): Promise<void> {
    const userDocRef = doc(db, 'users', userId)
    
    await setDoc(userDocRef, {
      lastLoginAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true })
  },

  // Get users by organization
  async getByOrganization(organizationId: string): Promise<User[]> {
    const q = query(
      collection(db, 'users'),
      where('organizationIds', 'array-contains', organizationId)
    )
    
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => {
      const data = doc.data()
      return {
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        lastLoginAt: data.lastLoginAt?.toDate() || new Date()
      } as User
    })
  },

  // Search users by email or display name
  async search(searchTerm: string, limitCount: number = 10): Promise<User[]> {
    // Note: Firestore doesn't support full-text search, so this is a basic implementation
    // For production, consider using Algolia or similar service for better search
    const q = query(
      collection(db, 'users'),
      where('email', '>=', searchTerm.toLowerCase()),
      where('email', '<=', searchTerm.toLowerCase() + '\uf8ff'),
      limit(limitCount)
    )
    
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => {
      const data = doc.data()
      return {
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        lastLoginAt: data.lastLoginAt?.toDate() || new Date()
      } as User
    })
  },

  async toggleFavoriteDashboard(userId: string, dashboardId: string): Promise<void> {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      const userData = userDoc.data() as User; // User type from shared schema
      const currentFavorites = userData.favoriteDashboardIds || [];
      let updatedFavorites: string[];

      if (currentFavorites.includes(dashboardId)) {
        // Remove from favorites
        updatedFavorites = currentFavorites.filter(id => id !== dashboardId);
      } else {
        // Add to favorites
        updatedFavorites = [...currentFavorites, dashboardId];
      }
      
      await updateDoc(userDocRef, {
        favoriteDashboardIds: updatedFavorites,
        updatedAt: serverTimestamp()
      });
    } else {
      throw new Error("User not found to update favorite dashboards.");
    }
  },

  // Custom Color Palette Management
  async saveCustomPalette(userId: string, palette: {
    name: string;
    description?: string;
    colors: string[];
    createdAt: Date;
    updatedAt: Date;
  }): Promise<{ id: string }> {
    const userDocRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userDocRef)
    
    if (!userDoc.exists()) {
      throw new Error('User not found')
    }
    
    const paletteId = crypto.randomUUID()
    const currentPalettes = userDoc.data().customColorPalettes || []
    
    const newPalette = {
      id: paletteId,
      ...palette,
      createdAt: new Date(), // Use regular Date instead of serverTimestamp() inside arrays
      updatedAt: new Date()
    }
    
    await setDoc(userDocRef, {
      customColorPalettes: [...currentPalettes, newPalette],
      updatedAt: serverTimestamp()
    }, { merge: true })
    
    return { id: paletteId }
  },

  async updateCustomPalette(userId: string, paletteId: string, updates: {
    name?: string;
    description?: string;
    colors?: string[];
    updatedAt: Date;
  }): Promise<void> {
    const userDocRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userDocRef)
    
    if (!userDoc.exists()) {
      throw new Error('User not found')
    }
    
    const currentPalettes = userDoc.data().customColorPalettes || []
    const paletteIndex = currentPalettes.findIndex((p: any) => p.id === paletteId)
    
    if (paletteIndex === -1) {
      throw new Error('Palette not found')
    }
    
    const updatedPalettes = [...currentPalettes]
    updatedPalettes[paletteIndex] = {
      ...updatedPalettes[paletteIndex],
      ...updates,
      updatedAt: new Date() // Use regular Date instead of serverTimestamp() inside arrays
    }
    
    await setDoc(userDocRef, {
      customColorPalettes: updatedPalettes,
      updatedAt: serverTimestamp()
    }, { merge: true })
  },

  async deleteCustomPalette(userId: string, paletteId: string): Promise<void> {
    const userDocRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userDocRef)
    
    if (!userDoc.exists()) {
      throw new Error('User not found')
    }
    
    const currentPalettes = userDoc.data().customColorPalettes || []
    const updatedPalettes = currentPalettes.filter((p: any) => p.id !== paletteId)
    
    await setDoc(userDocRef, {
      customColorPalettes: updatedPalettes,
      updatedAt: serverTimestamp()
    }, { merge: true })
  },

  async getCustomPalettes(userId: string): Promise<any[]> {
    const userDocRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userDocRef)
    
    if (!userDoc.exists()) {
      return []
    }
    
    const palettes = userDoc.data().customColorPalettes || []
    return palettes.map((palette: any) => ({
      ...palette,
      createdAt: palette.createdAt?.toDate() || new Date(),
      updatedAt: palette.updatedAt?.toDate() || new Date()
    }))
  }
}

// Utility function to generate slug from name
export const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 50)
} 